import { clearWindowRefreshJwt, setWindowJwtToken } from '@/hooks/utils/window/jwtToken';
import { appLogger } from '@/utils/app-logger/appLogger';
import {
    isNewestAuthoritativePublish,
    isNewestRestartTicket,
    markAuthoritativePublished,
    markRestartPublished,
} from '@/utils/session/restartTicket';

type WindowWithAuthMark = typeof window & {
    // Set once a signed-in session is published; guards against a later anonymous Start downgrading it.
    sessionIsAuthenticated?: boolean;
};

// `authenticated` (sign-in/up) and `anonymous` (sign-out, client Start) set the auth marker;
// `inherit` (anonymous restart carrying the token forward, SSR initial publish) leaves it as-is.
type SessionIdentity = 'authenticated' | 'anonymous' | 'inherit';

/**
 * Arms the auth marker without publishing a token — for when an authoritative authenticated restart
 * is deferred (e.g. skipped because capability cookies aren't ready yet). Keeps a later stray
 * anonymous Start from downgrading a just-authenticated user before the real token is published.
 */
export function markSessionAuthenticated(): void {
    if (typeof window === 'undefined') return;
    (window as WindowWithAuthMark).sessionIsAuthenticated = true;
}

/**
 * Clears the auth marker without publishing a token — for when an authoritative anonymous flow
 * (sign-out) is deferred (its restart skipped because capabilities aren't ready). Mirrors
 * `markSessionAuthenticated` so a just-logged-out user isn't left flagged as authenticated.
 */
export function markSessionAnonymous(): void {
    if (typeof window === 'undefined') return;
    (window as WindowWithAuthMark).sessionIsAuthenticated = false;
}

/**
 * True once an authenticated session has been marked — whether its token was published or the
 * publish was deferred (e.g. a sign-in restart skipped for unready capabilities). Lets a recovery
 * path prefer an authenticated restart over an anonymous Start that the downgrade guard would refuse.
 */
export function isSessionExpectedAuthenticated(): boolean {
    if (typeof window === 'undefined') return false;
    return (window as WindowWithAuthMark).sessionIsAuthenticated === true;
}

export type PublishSessionJwtOptions = {
    // Taken by the caller at the restart START (so a slow restart can be superseded) or right before
    // a Start/SSR publish (a fresh, highest ticket that always wins).
    ticket: number;
    // Auth-change flows: publish unconditionally so a later-ticketed anonymous Start can't shut them out.
    authoritative?: boolean;
    identity?: SessionIdentity;
    // A brand-new anonymous session (not a refresh); refused when an authenticated session is published.
    freshAnonymousMint?: boolean;
};

/**
 * Single funnel for every `window.jwtToken` write — centralises latest-wins ticket gating, the
 * authoritative bypass, the auth→anonymous downgrade guard, and cold-start seed retirement so
 * overlapping publishers can't each race a slightly different copy of these rules.
 *
 * @returns whether the token was published (false when gated out, refused, or empty).
 */
export function publishSessionJwt(
    nextJwt: string,
    {
        ticket,
        authoritative = false,
        identity = 'inherit',
        freshAnonymousMint = false,
    }: PublishSessionJwtOptions,
): boolean {
    if (typeof window === 'undefined') return false;

    const token = nextJwt.trim();
    if (!token) return false;

    const windowWithAuth = window as WindowWithAuthMark;

    // A fresh Start mints anonymous, so it must not overwrite a signed-in token; sign-out downgrades
    // deliberately, but it's authoritative and not a fresh mint, so it isn't caught here.
    if (freshAnonymousMint && windowWithAuth.sessionIsAuthenticated) {
        appLogger.warn(
            'Session JWT publish suppressed; a fresh anonymous Start would downgrade an authenticated session',
            { component: 'publishSessionJwt' },
        );
        return false;
    }

    // Authoritative publishes bypass the non-authoritative gate but are still ordered among
    // themselves, so a stale recovery can't overwrite a newer authoritative sign-out/sign-in.
    if (authoritative) {
        if (!isNewestAuthoritativePublish(ticket)) {
            appLogger.warn(
                'Session JWT publish suppressed; a newer authoritative publish already won',
                { component: 'publishSessionJwt' },
            );
            return false;
        }
    } else if (!isNewestRestartTicket(ticket)) {
        return false;
    }

    markRestartPublished(ticket);
    if (authoritative) markAuthoritativePublished(ticket);
    setWindowJwtToken(token);
    // Retire the cold-start seed so a later token loss recovers via Start, not the stale incomplete JWT.
    clearWindowRefreshJwt();

    if (identity === 'authenticated') {
        markSessionAuthenticated();
    } else if (identity === 'anonymous') {
        markSessionAnonymous();
    }

    return true;
}
